import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { SimpleSelect } from '@/components/surveyAccess/accessProfileFields';

const NONE = 'none';
const METHOD_OPTIONS = [[NONE, '—'], ['qr', 'QR'], ['manual', 'Numeric']];
const key = (v) => (v || '').trim().toUpperCase();

export const mappableBranches = (branches) => (branches || []).filter(b => b.branch_classification !== 'competitor');

// Drop rows without a store code; with only one method on, that method is implied.
export const cleanMapping = (mapping, methods) => {
  const only = methods.qr && !methods.manual ? 'qr' : (methods.manual && !methods.qr ? 'manual' : '');
  return (mapping || []).filter(m => (m.external_id || '').trim())
    .map(m => ({ external_id: m.external_id.trim(), branch_id: m.branch_id, access_method: m.access_method || only || null }));
};

// Phase 4B — one row per existing branch: External / POS Store Code + QR/Numeric.
export const BranchMappingEditor = ({ form, set, branches }) => {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => form.branch_mapping || [], [form.branch_mapping]);
  const dupCodes = useMemo(() => {
    const counts = {};
    rows.forEach(r => { const k = key(r.external_id); if (k) counts[k] = (counts[k] || 0) + 1; });
    return new Set(Object.keys(counts).filter(k => counts[k] > 1));
  }, [rows]);
  const upsert = (branchId, patch) => {
    const i = rows.findIndex(r => r.branch_id === branchId);
    set(['branch_mapping'], i === -1
      ? [...rows, { branch_id: branchId, external_id: '', access_method: '', ...patch }]
      : rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const all = mappableBranches(branches);
  const q = query.trim().toLowerCase();
  const list = all.filter(b => `${b.name} ${b.branch_code || ''}`.toLowerCase().includes(q));
  const mapped = rows.filter(r => key(r.external_id)).length;

  return (
    <div className="space-y-3" data-testid="access-branch-mapping">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-[#334155]">
          <b data-testid="access-mapped-count">{mapped}</b> of {all.length} branches have a store code. Every branch inherits this profile's rules.
        </p>
        <div className="relative w-56">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a branch" className="pl-8 h-9" data-testid="access-map-search" />
        </div>
      </div>
      <div className="max-h-[42vh] overflow-y-auto rounded-lg border border-[#E2E8F0]">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10 bg-[#F8FAFC] text-[#475569]">
            <tr>
              <th className="text-left font-semibold px-3 py-2">Existing branch in satisFIND</th>
              <th className="text-left font-semibold px-3 py-2 w-48">External / POS Store Code</th>
              <th className="text-left font-semibold px-3 py-2 w-36">Access Method</th>
            </tr>
          </thead>
          <tbody>
            {list.map(b => {
              const e = rows.find(r => r.branch_id === b.branch_id) || {};
              const dup = dupCodes.has(key(e.external_id));
              return (
                <tr key={b.branch_id} className="border-t border-[#F1F5F9]" data-testid={`access-map-row-${b.branch_id}`}>
                  <td className="px-3 py-1.5 text-[#0F172A]">
                    {b.name}{b.branch_code && <span className="ml-1.5 text-[10px] text-[#94A3B8]">{b.branch_code}</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    <Input value={e.external_id || ''} onChange={(ev) => upsert(b.branch_id, { external_id: ev.target.value })}
                      placeholder="e.g. 360" className={`h-8 ${dup ? 'border-[#DC2626]' : ''}`} data-testid={`access-map-code-${b.branch_id}`} />
                    {dup && <p className="text-[10px] text-[#DC2626] mt-0.5" data-testid={`access-map-dup-${b.branch_id}`}>Used by another branch</p>}
                  </td>
                  <td className="px-3 py-1.5">
                    <SimpleSelect value={e.access_method || NONE} onChange={(v) => upsert(b.branch_id, { access_method: v === NONE ? '' : v })}
                      options={METHOD_OPTIONS} testid={`access-map-method-${b.branch_id}`} />
                  </td>
                </tr>
              );
            })}
            {!list.length && (
              <tr><td colSpan={3} className="px-3 py-6 text-center text-[#94A3B8]" data-testid="access-map-empty">No branches match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
